<template>
  <card-container
    :id="pageId"
    :class="pageId"
    :is-loading="isLoading"
    :disabled="isLoading"
    loading-message="Saving assignment..."
    @submit="submitForm"
    @reset="resetForm"
  >
    <template #title>
      Coaching Assistant
    </template>

    <template #card-notes>
      <div class="card-notes">
        Configure AI monitoring for operator improvement. The assistant will monitor calls for the selected coaching focus during the specified period.
      </div>
    </template>

    <template #content>
      <!-- Operator -->
      <div class="row align-items-center">
        <label class="col-sm-3 control-label">Operator*</label>
        <div class="col-sm-8">
          <el-select
            v-model="form.operatorId"
            filterable
            remote
            :remote-method="searchOperators"
            :loading="isSearchingOperators"
            placeholder="Type to search operators..."
            size="large"
          >
            <el-option
              v-for="op in operatorOptions"
              :key="op.id"
              :label="op.label"
              :value="op.id"
            />
          </el-select>
        </div>
      </div>

      <!-- Coaching Focus -->
      <div class="row align-items-center">
        <label class="col-sm-3 control-label">Coaching Focus*</label>
        <div class="col-sm-8">
          <dropdown-list
            v-model="form.agentId"
            :options="coachingAgents"
            value-key="id"
            label-key="displayName"
            :required="true"
          />
          <span class="help-block">The assistant will monitor calls specifically for this behavior.</span>
        </div>
      </div>

      <!-- Start Date -->
      <div class="row align-items-center">
        <label class="col-sm-3 control-label">Start Date*</label>
        <div class="col-sm-8">
          <date-picker
            v-model="form.startDate"
            placeholder="mm/dd/yyyy"
            format="MM/DD/YYYY"
            value-format="YYYY-MM-DD"
            :required="true"
          />
        </div>
      </div>

      <!-- End Date -->
      <div class="row align-items-center">
        <label class="col-sm-3 control-label">End Date*</label>
        <div class="col-sm-8">
          <date-picker
            v-model="form.endDate"
            placeholder="mm/dd/yyyy"
            format="MM/DD/YYYY"
            value-format="YYYY-MM-DD"
            :required="true"
          />
        </div>
      </div>

      <!-- Service Type -->
      <div class="row">
        <label class="col-sm-3 control-label pt-10">Service Type*</label>
        <div class="col-sm-8">
          <el-checkbox-group v-model="form.serviceTypes">
            <el-checkbox
              v-for="option in serviceTypeOptions"
              :key="option.value"
              :label="option.value"
            >
              {{ option.label }}
            </el-checkbox>
          </el-checkbox-group>
        </div>
      </div>

      <!-- Caller Type -->
      <div class="row">
        <label class="col-sm-3 control-label pt-10">Caller Type*</label>
        <div class="col-sm-8">
          <el-checkbox-group v-model="form.callerTypes">
            <el-checkbox
              v-for="option in callerTypeOptions"
              :key="option.value"
              :label="option.value"
            >
              {{ option.label }}
            </el-checkbox>
          </el-checkbox-group>
        </div>
      </div>
    </template>

    <template #submit-button>
      Start Monitoring
    </template>
  </card-container>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue';
import moment from 'moment-timezone';
import { ElNotification } from 'element-plus';
import { fetchOperators } from '@/services/operators/fetchOperators';
import { fetchCoachingAgents, CoachingAgent } from '@/services/coaching/fetchCoachingAgents';
import { createCoachingAssignment } from '@/services/coaching/createCoachingAssignment';
import events from '@/events';
const pageId = 'coaching-assistant';

const serviceTypeOptions = [
  { value: 'operations', label: 'Rides' },
  { value: 'gourmet', label: 'Gourmet' },
  { value: 'groceries', label: 'Grocery' },
  { value: 'homeServices', label: 'Home Services' },
];

const callerTypeOptions = [
  { value: 'new_reg', label: 'New Reg' },
  { value: 'soft_reg', label: 'Soft Reg' },
  { value: 'existing', label: 'Existing Customer' },
  { value: 'deactivated', label: 'Deactivated Customer' },
];

const operators = ref<{ id: string; firstName?: string; lastName?: string }[]>([]);
const operatorOptions = computed(() => operators.value.map((op) => ({ id: op.id, label: `${op.firstName} ${op.lastName}` })));
const coachingAgents = ref<CoachingAgent[]>([]);
const isLoading = ref(false);
const isSearchingOperators = ref(false);

const form = ref({
  operatorId: '' as string,
  agentId: '' as string,
  startDate: moment.utc().format('YYYY-MM-DD'),
  endDate: moment.utc().add(1, 'week').format('YYYY-MM-DD'),
  serviceTypes: [] as string[],
  callerTypes: [] as string[],
});

const searchOperators = async (query: string) => {
  if (!query) {
    operators.value = [];
    return;
  }

  isSearchingOperators.value = true;
  try {
    const result = await fetchOperators({ isActive: true, query }, 'network-only');
    operators.value = result.rows;
  } finally {
    isSearchingOperators.value = false;
  }
};

onMounted(async () => {
  try {
    coachingAgents.value = await fetchCoachingAgents();
  } catch {
    ElNotification({ type: 'error', message: 'Failed to load form data.' });
  }
});

const submitForm = async () => {
  if (!form.value.serviceTypes.length || !form.value.callerTypes.length) {
    ElNotification({ type: 'warning', message: 'Please fill in all required fields.' });
    return;
  }

  isLoading.value = true;
  try {
    await createCoachingAssignment({
      agentId: form.value.agentId,
      operatorId: form.value.operatorId,
      startDate: form.value.startDate,
      endDate: form.value.endDate,
      data: {
        serviceType: form.value.serviceTypes.join(','),
        callerType: form.value.callerTypes.join(','),
      },
    });

    ElNotification({ type: 'success', message: 'Coaching assignment created successfully.' });
    events.emit('coaching-assignment-created');
    resetForm();
  } catch {
    ElNotification({ type: 'error', message: 'Failed to create coaching assignment. Please try again.' });
  } finally {
    isLoading.value = false;
  }
};

const resetForm = () => {
  form.value = {
    operatorId: '',
    agentId: '',
    startDate: moment.utc().format('YYYY-MM-DD'),
    endDate: moment.utc().add(1, 'week').format('YYYY-MM-DD'),
    serviceTypes: [],
    callerTypes: [],
  };
};
</script>
